import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import '../styles/pages/PhotographerCompleteProfilePage.css';

const PhotographerCompleteProfilePage = () => {
    const navigate = useNavigate();
    const { userAttributes, fetchUserAttributes, authFetch } = useAuth();

    const predefinedSpecialities = [
        "Weddings",
        "Portrait",
        "Events",
        "Commercial",
        "Fashion",
        "Product",
        "Real Estate",
        "Food",
        "Travel",
        "Lifestyle",
        "Sports",
        "Newborn & Maternity",
        "Pets",
        "Architectural",
        "Fine Art",
        "Aerial/Drone",
        "Corporate",
        "Boudoir",
        "Automotive",
        "Other"
    ];

    const [selectedSpecialities, setSelectedSpecialities] = useState([]);
    const [customSpecialities, setCustomSpecialities] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleCheckboxChange = (speciality) => {
        setSelectedSpecialities((prev) =>
            prev.includes(speciality)
                ? prev.filter((item) => item !== speciality)
                : [...prev, speciality]
        );
    };

    const saveProfile = async () => {
        const combinedSpecialities = [
            ...selectedSpecialities.map((speciality) => ({
                speciality,
                is_custom: false,
            })),
            ...customSpecialities
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
                .map((speciality) => ({ speciality, is_custom: true })),
        ];

        setIsSaving(true);
        setErrorMessage('');

        try {
            const attributes = userAttributes.sub
                ? userAttributes
                : await fetchUserAttributes();

            if (!attributes.sub) {
                throw new Error('Unable to identify the signed-in photographer.');
            }

            const response = await authFetch(
                `https://7ivi6888a9.execute-api.eu-west-2.amazonaws.com/dev/photographers/${attributes.sub}/specialities`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ specialities: combinedSpecialities }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.message || `Failed to save profile (${response.status})`
                );
            }

            return true;
        } catch (error) {
            console.error('Error saving photographer profile:', error);
            setErrorMessage(error.message || 'Failed to save your profile. Please try again.');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveAndCompletePayouts = async () => {
        const saved = await saveProfile();
        if (saved) {
            navigate('/stripe-account-signup-embedded');
        }
    };

    const handleSaveAndSkipPayouts = async () => {
        const saved = await saveProfile();
        if (saved) {
            navigate('/');
        }
    };

    const handleSkipProfile = () => {
        navigate('/');
    };

    return (
        <main className="complete-profile-page">
            <section className="complete-profile-card">
                <header className="complete-profile-intro">
                    <span className="complete-profile-kicker">Photographer setup</span>
                    <h1>Set up your photographer profile</h1>
                    <p>
                        Choose the kinds of photography work you'd like clients to find you for.
                        You can update these later as your profile develops.
                    </p>
                </header>

                <div className="complete-profile-section">
                    <div className="complete-profile-section-heading">
                        <h2>What kind of work do you want to be discovered for?</h2>
                        <p>Select as many as apply.</p>
                    </div>

                    <div className="specialities-grid">
                        {predefinedSpecialities.map((speciality) => {
                            const selected = selectedSpecialities.includes(speciality);

                            return (
                                <label
                                    key={speciality}
                                    className={`speciality-item ${selected ? 'is-selected' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        value={speciality}
                                        checked={selected}
                                        onChange={() => handleCheckboxChange(speciality)}
                                    />
                                    <span>{speciality}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>

                <div className="complete-profile-section">
                    <div className="complete-profile-section-heading">
                        <h2>Anything else?</h2>
                        <p>Add custom work types separated by commas.</p>
                    </div>

                    <textarea
                        className="custom-specialities-input"
                        value={customSpecialities}
                        onChange={(e) => setCustomSpecialities(e.target.value)}
                        placeholder="e.g. Live music, Theatre, Editorial portraits"
                        rows={3}
                    />
                </div>

                <div className="onboarding-actions">
                    <div className="onboarding-actions__primary">
                        <Button
                            type="button"
                            variant="primary"
                            size="lg"
                            onClick={handleSaveAndCompletePayouts}
                            disabled={isSaving}
                            className="onboarding-action-button"
                        >
                            {isSaving ? 'Saving...' : 'Save & Complete Payout Setup'}
                        </Button>

                        <Button
                            type="button"
                            variant="secondary"
                            size="lg"
                            onClick={handleSaveAndSkipPayouts}
                            disabled={isSaving}
                            className="onboarding-action-button"
                        >
                            Save & Skip Payout Setup
                        </Button>
                    </div>

                    <button
                        type="button"
                        className="skip-profile-button"
                        onClick={handleSkipProfile}
                        disabled={isSaving}
                    >
                        Skip Profile Setup For Now
                    </button>
                </div>

                <div className="onboarding-note">
                    <p>
                        You can finish your profile and payout setup later. A completed photographer
                        profile will be required before you can be discoverable to clients, and payout
                        setup will be required before accepting paid bookings.
                    </p>
                </div>

                {errorMessage && <div className="error-message">{errorMessage}</div>}
            </section>
        </main>
    );
};

export default PhotographerCompleteProfilePage;
